import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type Primitive = string | number | boolean;
type PrimitiveArr = Primitive[];
export type ParamType = Primitive | PrimitiveArr;

interface UrlParamOptions {
  /** Replaces the current history entry instead of pushing a new one. */
  replace?: boolean;
}

/**
 * Sync a single query-string parameter with component state.
 * Works like `useState`, but persists the value in the URL.
 */
export default function useUrlParam<T extends ParamType>(
  key: string,
  defaultValue?: T,
  { replace = false }: UrlParamOptions = {}
): [T, (v: T) => void] {
  const location = useLocation();
  const navigate = useNavigate();

  /* ---------- helpers ---------- */

  /** Coerce a string from the URL into the right runtime type. */
  const castFromString = (raw: string): Primitive => {
    if (raw === "true" || raw === "false") return raw === "true";

    if (typeof defaultValue === "number") return Number(raw);
    if (typeof defaultValue === "boolean") return raw === "true";

    return raw;
  };

  const stringify = (v: Primitive) => (typeof v === "boolean" ? String(v) : `${v}`);

  /* ---------- read ---------- */

  const value = useMemo<T>(() => {
    const search = new URLSearchParams(location.search);
    const all = search.getAll(key);

    if (all.length === 0) return defaultValue as T;

    if (Array.isArray(defaultValue)) {
      return all.map(castFromString) as unknown as T;
    }

    // When defaultValue is missing we still want booleans handled properly
    return castFromString(all[0]) as unknown as T;
  }, [location.search, key, defaultValue]);

  /* ---------- write ---------- */

  const setValue = useCallback(
    (newValue: T) => {
      const search = new URLSearchParams(location.search);

      if (newValue === defaultValue) {
        search.delete(key);
      } else if (Array.isArray(newValue)) {
        search.delete(key);
        newValue.forEach((v) => search.append(key, stringify(v as Primitive)));
      } else {
        search.set(key, stringify(newValue as Primitive));
      }

      const nextSearch = search.toString();
      const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;

      if (nextUrl !== `${location.pathname}${location.search}`) {
        navigate(nextUrl, { replace });
      }
    },
    [key, location.pathname, location.search, navigate, replace]
  );

  return [value, setValue];
}
